import * as THREE from 'three'

/**
 * Recursive GPU teardown for a detachable scene.
 *
 * The region world is built once and lives for the session, so nothing in the
 * codebase needed this before. A ground zone is mounted and unmounted
 * repeatedly, and every entry that does not fully release its predecessor leaks
 * VRAM until the tab dies. This is the piece that makes a second scene safe.
 */

function disposeMaterial(material: THREE.Material): void {
  for (const value of Object.values(material)) {
    // Covers map, normalMap, alphaMap, envMap and any custom slots without
    // having to enumerate them, which drifts as materials change.
    if (value instanceof THREE.Texture) value.dispose()
  }
  material.dispose()
}

export function disposeObject(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>()
  const materials = new Set<THREE.Material>()

  root.traverse((node) => {
    const withGeometry = node as Partial<THREE.Mesh>
    if (withGeometry.geometry) geometries.add(withGeometry.geometry)
    const material = (node as Partial<THREE.Mesh>).material
    if (Array.isArray(material)) for (const entry of material) materials.add(entry)
    else if (material) materials.add(material)

    // A shadow map is a render target hanging off the light rather than a
    // texture in the graph, so traversing for materials alone walks straight
    // past it and leaks one 2048² depth buffer on every mount.
    const light = node as Partial<THREE.DirectionalLight>
    if (light.shadow?.map) {
      light.shadow.map.dispose()
      light.shadow.map = null
    }
    light.shadow?.dispose?.()
  })

  for (const geometry of geometries) geometry.dispose()
  for (const material of materials) disposeMaterial(material)

  root.removeFromParent()
  root.clear()
}

export function disposeScene(scene: THREE.Scene): void {
  disposeObject(scene)
  if (scene.background instanceof THREE.Texture) scene.background.dispose()
  if (scene.environment instanceof THREE.Texture) scene.environment.dispose()
  scene.background = null
  scene.environment = null
}

export interface GpuCounts {
  geometries: number
  textures: number
  programs: number
}

/**
 * Read straight off the renderer so a leak is measurable rather than argued
 * about. Enter and exit a zone repeatedly and these must return to baseline.
 */
export function gpuCounts(renderer: THREE.WebGLRenderer): GpuCounts {
  return {
    geometries: renderer.info.memory.geometries,
    textures: renderer.info.memory.textures,
    programs: renderer.info.programs?.length ?? 0,
  }
}
